// Canned Messages Service
// Stores custom DJ message templates on the user document in Firestore

import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from './firebase';

export interface CannedMessage {
  id: string;
  label: string;
  text: string;
}

// Default seed templates (used when DJ has no saved templates)
const DEFAULT_TEMPLATES: CannedMessage[] = [
  { id: 'more-songs', label: 'We need more songs!', text: 'We need more song requests! Submit your favorite tracks now!' },
  { id: 'last-call', label: 'Last call for requests', text: 'Last call for song requests! Get your requests in before we close voting.' },
  { id: 'thank-you', label: 'Thank you', text: 'Thank you all for being here tonight! Keep the requests coming!' },
  { id: 'taking-break', label: 'Taking a short break', text: 'Taking a short break. Keep voting on your favorite songs!' },
  { id: 'voting-closed', label: 'Voting is now closed', text: 'Voting is now closed. Thanks for all your requests!' },
];

// Get canned messages for a DJ (seeds defaults on first load)
export async function getCannedMessages(djId: string): Promise<CannedMessage[]> {
  const userRef = doc(db, 'users', djId);
  const snap = await getDoc(userRef);
  if (snap.exists() && snap.data().cannedMessages) {
    return snap.data().cannedMessages;
  }
  // Seed with defaults on first access
  await setDoc(userRef, { cannedMessages: DEFAULT_TEMPLATES }, { merge: true });
  return DEFAULT_TEMPLATES;
}

// Save full list of canned messages
export async function saveCannedMessages(djId: string, messages: CannedMessage[]): Promise<void> {
  const userRef = doc(db, 'users', djId);
  await setDoc(userRef, { cannedMessages: messages }, { merge: true });
}

// Add a single canned message
export async function addCannedMessage(djId: string, label: string, text: string): Promise<CannedMessage> {
  const current = await getCannedMessages(djId);
  const newMsg: CannedMessage = {
    id: `custom-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`,
    label,
    text,
  };
  const updated = [...current, newMsg];
  await saveCannedMessages(djId, updated);
  return newMsg;
}

// Delete a canned message
export async function deleteCannedMessage(djId: string, messageId: string): Promise<void> {
  const current = await getCannedMessages(djId);
  const updated = current.filter(m => m.id !== messageId);
  await saveCannedMessages(djId, updated);
}
